import { useRef, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'

/**
 * Parti comuni delle finestre di importazione da un tracciato .xlsx a tendine: i mediatori nuovi
 * (elenchi/ImportaMediatori.tsx, 30/10/2026) e le schede con dati anonimizzati
 * (ImportaAnonimizzate.tsx, 31/10/2026). Stesso flusso, come le rotte dell'API: il foglio caricato in
 * base64, l'anteprima riga per riga, l'inclusione o l'esclusione delle righe (che si ricontrollano:
 * l'esito di una riga dipende dalle altre incluse), la conferma. Se nel frattempo una riga non va piu',
 * la conferma risponde 409 RIGHE_DA_CORREGGERE con l'anteprima aggiornata e non entra niente.
 */

/** Il file caricato, come il limite dell'API (servizi/foglio.ts). */
export const MASSIMO_BYTE = 3 * 1024 * 1024

export const messaggioErrore = (e: unknown) =>
  e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'

/** Il file in base64, a pezzi: `btoa` su un file intero supera la pila con i file grandi. */
export async function inBase64(file: File): Promise<string> {
  const byte = new Uint8Array(await file.arrayBuffer())
  let testo = ''
  for (let i = 0; i < byte.length; i += 0x8000) testo += String.fromCharCode(...byte.subarray(i, i + 0x8000))
  return btoa(testo)
}

/** Una riga dell'anteprima: quello che serve al flusso. Ogni importazione aggiunge i suoi campi. */
export type RigaImportazione = { numero: number; valori: Record<string, string>; stato: string; errori: string[]; avvisi: string[] }
export type AnteprimaImportazione<R> = { avvisi?: string[]; colonne_ignorate?: string[]; righe_nel_foglio?: number; righe: R[] }

/**
 * Stato e azioni della finestra. `indirizzo` e' la rotta della conferma; l'anteprima e'
 * `${indirizzo}/anteprima`, il tracciato `${indirizzo}/tracciato`.
 */
export function useImportazione<R extends RigaImportazione, E>({ indirizzo, esclusaAllInizio, importati }: {
  indirizzo: string
  /** Le righe che partono escluse: si importa solo cio' che e' pronto. */
  esclusaAllInizio: (r: R) => boolean
  /** Dopo la conferma: l'elenco si ricarica. La finestra resta aperta sull'esito. */
  importati: () => void
}) {
  const [fase, setFase] = useState<'foglio' | 'anteprima' | 'esito'>('foglio')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [nomeFile, setNomeFile] = useState('')
  const [anteprima, setAnteprima] = useState<AnteprimaImportazione<R> | null>(null)
  const [escluse, setEscluse] = useState<Set<number>>(new Set())
  const [ricontrollo, setRicontrollo] = useState(false)
  const [esito, setEsito] = useState<E | null>(null)
  const ingresso = useRef<HTMLInputElement>(null)

  async function leggi(file: File) {
    setErrore(null)
    if (file.size > MASSIMO_BYTE) {
      setErrore('Il file è troppo grande: un foglio di qualche centinaio di righe pesa molto meno.')
      return
    }
    setInCorso(true)
    try {
      const a = await api.post<AnteprimaImportazione<R>>(`${indirizzo}/anteprima`,
        { nome_file: file.name, contenuto: await inBase64(file) })
      setNomeFile(file.name)
      setAnteprima(a)
      setEscluse(new Set(a.righe.filter(esclusaAllInizio).map((r) => r.numero)))
      setFase('anteprima')
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
      if (ingresso.current) ingresso.current.value = ''
    }
  }

  /** Includere o escludere una riga cambia l'esito delle altre (la stessa persona in due righe). */
  async function cambiaInclusione(numero: number, includi: boolean) {
    if (!anteprima) return
    const nuove = new Set(escluse)
    if (includi) nuove.delete(numero); else nuove.add(numero)
    setEscluse(nuove)
    const incluse = anteprima.righe.filter((r) => !nuove.has(r.numero))
    if (!incluse.length) return
    setRicontrollo(true); setErrore(null)
    try {
      const a = await api.post<AnteprimaImportazione<R>>(`${indirizzo}/anteprima`,
        { righe: incluse.map((r) => ({ numero: r.numero, ...r.valori })) })
      const perNumero = new Map(a.righe.map((r) => [r.numero, r]))
      setAnteprima((x) => x && { ...x, righe: x.righe.map((r) => perNumero.get(r.numero) ?? r) })
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setRicontrollo(false)
    }
  }

  async function importa() {
    if (!anteprima) return
    setErrore(null); setInCorso(true)
    const incluse = anteprima.righe.filter((r) => !escluse.has(r.numero))
    try {
      const r = await api.post<E>(indirizzo, {
        righe: incluse.map((x) => ({ numero: x.numero, ...x.valori })),
        righe_nel_foglio: anteprima.righe_nel_foglio ?? anteprima.righe.length,
      })
      setEsito(r)
      setFase('esito')
      importati()
    } catch (e) {
      // Nel frattempo una riga non va piu': torna l'anteprima aggiornata e non e' entrato niente.
      if (e instanceof ErroreApi && e.codice === 'RIGHE_DA_CORREGGERE') {
        const perNumero = new Map((e.corpo as { righe: R[] }).righe.map((x) => [x.numero, x]))
        setAnteprima((a) => a && { ...a, righe: a.righe.map((x) => perNumero.get(x.numero) ?? x) })
      }
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  /** «Carica un altro foglio»: si torna al primo passo. */
  function ricomincia() {
    setFase('foglio'); setAnteprima(null); setErrore(null)
  }

  return {
    fase, errore, inCorso, nomeFile, anteprima, escluse, ricontrollo, esito, ingresso,
    leggi, cambiaInclusione, importa, ricomincia,
  }
}
